import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
  Select,
  FormControl,
  InputLabel,
  Popover,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  AccountCircle,
  Settings,
  Help,
  Logout,
  Business,
  Hotel,
  ChevronLeft,
  ChevronRight,
  SmartToy,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useAuthStore } from '../stores/authStore';
import { useNavigationStore } from '../stores/navigationStore';

interface TopNavigationProps {
  onChatbotToggle?: (isOpen: boolean) => void;
}

const TopNavigation: React.FC<TopNavigationProps> = ({ onChatbotToggle }) => {
  const { logout } = useAuthStore();
  const { currentScreen } = useNavigationStore();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] = React.useState<null | HTMLElement>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedAccount, setSelectedAccount] = useState('hotel-continental');
  const [selectedProperty, setSelectedProperty] = useState('main-branch');
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [dateTimeAnchorEl, setDateTimeAnchorEl] = useState<HTMLElement | null>(null);

  // Mock data for accounts and properties
  const accounts = [
    { id: 'hotel-continental', name: 'Hotel Continental', type: 'hotel' },
    { id: 'restaurant-chain', name: 'Restaurant Chain', type: 'restaurant' },
    { id: 'resort-group', name: 'Resort Group', type: 'resort' },
  ];

  const properties = [
    { id: 'main-branch', name: 'Main Branch', address: '123 Main Street, City' },
    { id: 'downtown-branch', name: 'Downtown Branch', address: '456 Downtown Ave, City' },
    { id: 'airport-branch', name: 'Airport Branch', address: '789 Airport Road, City' },
    { id: 'beach-resort', name: 'Beach Resort', address: '321 Beach Boulevard, City' },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);


  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchor(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
  };

  const handleDateTimeClick = (event: React.MouseEvent<HTMLElement>) => {
    setDateTimeAnchorEl(event.currentTarget);
  };

  const handleDateTimeClose = () => {
    setDateTimeAnchorEl(null);
  };

  const handlePreviousMonth = () => {
    setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1));
  };

  const handleChatbotOpen = () => {
    onChatbotToggle?.(true);
  };

  // Format time and date for modern display
  const time12Hour = format(currentTime, 'hh:mm:ss');
  const time24Hour = format(currentTime, 'HH:mm:ss');
  const currentDate = format(currentTime, 'EEEE, MMMM do, yyyy');
  const shortDate = format(currentTime, 'MMM dd');
  const dayOfWeek = format(currentTime, 'EEEE');
  const dayOfMonth = format(currentTime, 'do');
  const monthYear = format(currentTime, 'MMMM yyyy');

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(214, 149, 36, 0.2)',
        color: 'text.primary',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', px: 2, py: 0.5, minHeight: '48px', height: '48px' }}>
        {/* Left side - Clean Date and Time Display */}
        <Box sx={{ position: 'relative' }} data-datetime-popup>
          <Tooltip title={`${currentDate} at ${time12Hour}`}>
            <Box 
              onClick={handleDateTimeClick}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                cursor: 'pointer',
                px: 2,
                py: 1,
                borderRadius: 2,
                background: 'linear-gradient(135deg, rgba(214, 149, 36, 0.1) 0%, rgba(214, 149, 36, 0.05) 100%)',
                border: '1px solid rgba(214, 149, 36, 0.2)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(214, 149, 36, 0.15) 0%, rgba(214, 149, 36, 0.08) 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(214, 149, 36, 0.2)',
                }
              }}
            >
            {/* Date Display */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: 'primary.main',
                  fontSize: '14px',
                  lineHeight: 1.2,
                  textShadow: '0 1px 2px rgba(214, 149, 36, 0.3)',
                }}
              >
                {dayOfWeek}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '12px',
                  fontWeight: 500,
                  opacity: 0.8,
                }}
              >
                {shortDate}, {format(currentTime, 'yyyy')}
              </Typography>
            </Box>

            {/* Time Display */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: 'primary.main',
                    fontSize: '18px',
                    fontFamily: 'monospace',
                    lineHeight: 1,
                    textShadow: '0 1px 2px rgba(214, 149, 36, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  {time24Hour}
                </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '10px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  opacity: 0.7,
                }}
              >
                {format(currentTime, 'a')}
              </Typography>
            </Box>
            </Box>
          </Tooltip>

          {/* Calendar Popover */}
          <Popover
            open={Boolean(dateTimeAnchorEl)}
            anchorEl={dateTimeAnchorEl}
            onClose={handleDateTimeClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                border: '2px solid #d69524',
                minWidth: '320px',
                overflow: 'hidden',
              },
            }}
          >
            <Box sx={{ p: 3 }}>
              {/* Calendar Header with Navigation */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: 'primary.main',
                    textAlign: 'center',
                    flex: 1,
                  }}
                >
                  {format(calendarDate, 'MMMM yyyy')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Box
                    onClick={handlePreviousMonth}
                    sx={{
                      cursor: 'pointer',
                      p: 0.5,
                      borderRadius: 1,
                      '&:hover': { background: 'grey.100' },
                    }}
                  >
                    <ChevronLeft sx={{ color: 'primary.main' }} />
                  </Box>
                  <Box
                    onClick={handleNextMonth}
                    sx={{
                      cursor: 'pointer',
                      p: 0.5,
                      borderRadius: 1,
                      '&:hover': { background: 'grey.100' },
                    }}
                  >
                    <ChevronRight sx={{ color: 'primary.main' }} />
                  </Box>
                </Box>
              </Box>

              {/* Calendar Grid */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gap: 1,
                }}
              >
                {/* Day Headers */}
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => (
                  <Box
                    key={day}
                    sx={{
                      textAlign: 'center',
                      fontWeight: 700,
                      color: 'primary.main',
                      fontSize: '12px',
                      py: 1,
                      px: 0.5,
                      background: 'rgba(214, 149, 36, 0.1)',
                      borderRadius: 1,
                      minHeight: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {day}
                  </Box>
                ))}
                
                {/* Calendar Days */}
                {(() => {
                  const year = calendarDate.getFullYear();
                  const month = calendarDate.getMonth();
                  const today = new Date();
                  const isCurrentMonth = today.getMonth() === month && today.getFullYear() === year;
                  const todayDate = today.getDate();
                  
                  // Get first day of month and number of days
                  const firstDay = new Date(year, month, 1);
                  const lastDay = new Date(year, month + 1, 0);
                  const daysInMonth = lastDay.getDate();
                  const startDay = firstDay.getDay();
                  
                  // Convert Sunday (0) to Monday (1) start
                  const mondayStart = startDay === 0 ? 6 : startDay - 1;
                  
                  const days = [];
                  
                  // Add empty cells for days before month starts
                  for (let i = 0; i < mondayStart; i++) {
                    days.push(
                      <Box key={`empty-${i}`} sx={{ py: 1, px: 0.5 }} />
                    );
                  }
                  
                  // Add days of the month
                  for (let day = 1; day <= daysInMonth; day++) {
                    const isToday = isCurrentMonth && day === todayDate;
                    
                    days.push(
                      <Box
                        key={day}
                        sx={{
                          textAlign: 'center',
                          py: 1,
                          px: 0.5,
                          borderRadius: 1,
                          fontWeight: isToday ? 700 : 500,
                          color: isToday ? '#ffffff' : 'text.primary',
                          background: isToday ? '#d69524' : 'transparent',
                          border: isToday ? '2px solid #d69524' : 'none',
                          textShadow: isToday ? '0 1px 2px rgba(0, 0, 0, 0.3)' : 'none',
                        }}
                      >
                        {day}
                      </Box>
                    );
                  }
                  
                  return days;
                })()}
              </Box>
            </Box>
          </Popover>
        </Box>

        {/* Right side - Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Account Dropdown */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              value={selectedAccount}
              onChange={(e) => setSelectedAccount(e.target.value)}
              displayEmpty
              sx={{
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 1,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(214, 149, 36, 0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
              }}
            >
              {accounts.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Business sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2">{account.name}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Property Dropdown */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={selectedProperty}
              onChange={(e) => setSelectedProperty(e.target.value)}
              displayEmpty
              sx={{
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 1,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(214, 149, 36, 0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
              }}
            >
              {properties.map((property) => (
                <MenuItem key={property.id} value={property.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Hotel sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {property.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {property.address}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Notifications */}
          <Tooltip title="Notifications">
            <IconButton
              onClick={handleNotificationMenuOpen}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'rgba(214, 149, 36, 0.1)',
                  color: 'primary.main',
                },
              }}
            >
              <Badge badgeContent={3} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Profile Menu */}
          <Tooltip title="Account settings">
            <IconButton
              onClick={handleProfileMenuOpen}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'rgba(214, 149, 36, 0.1)',
                  color: 'primary.main',
                },
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'primary.main',
                  fontSize: '14px',
                  fontWeight: 'bold',
                }}
              >
                A
              </Avatar>
            </IconButton>
          </Tooltip>

          {/* AI Chatbot */}
          <Tooltip title="AI Assistant - Flurry">
            <Box
              onClick={handleChatbotOpen}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 3,
                py: 1.5,
                borderRadius: 3,
                cursor: 'pointer',
                background: 'linear-gradient(135deg, #d69524 0%, #f4a261 100%)',
                color: 'white',
                boxShadow: '0 4px 12px rgba(214, 149, 36, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #c0851f 0%, #e8954f 100%)',
                  boxShadow: '0 6px 20px rgba(214, 149, 36, 0.4)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                  transition: 'left 0.5s',
                },
                '&:hover::before': {
                  left: '100%',
                },
              }}
            >
              <img 
                src="/images/ai-agent.png" 
                alt="AI Agent" 
                style={{ 
                  width: 24, 
                  height: 24, 
                  objectFit: 'contain',
                }}
              />
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 600,
                  fontSize: '14px',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                }}
              >
                AI Agent
              </Typography>
            </Box>
          </Tooltip>
        </Box>

        {/* Profile Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 200,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(214, 149, 36, 0.2)',
            },
          }}
        >
          <MenuItem onClick={handleProfileMenuClose}>
            <AccountCircle sx={{ mr: 2, color: 'text.secondary' }} />
            Profile
          </MenuItem>
          <MenuItem onClick={handleProfileMenuClose}>
            <Settings sx={{ mr: 2, color: 'text.secondary' }} />
            Settings
          </MenuItem>
          <MenuItem onClick={handleProfileMenuClose}>
            <Help sx={{ mr: 2, color: 'text.secondary' }} />
            Help & Support
          </MenuItem>
          <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
            <Logout sx={{ mr: 2 }} />
            Logout
          </MenuItem>
        </Menu>

        {/* Notifications Menu */}
        <Menu
          anchorEl={notificationAnchor}
          open={Boolean(notificationAnchor)}
          onClose={handleNotificationMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 300,
              maxHeight: 400,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(214, 149, 36, 0.2)',
            },
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>
              Notifications
            </Typography>
          </Box>
          <MenuItem onClick={handleNotificationMenuClose}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                New booking request
              </Typography>
              <Typography variant="caption" color="text.secondary">
                2 minutes ago
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem onClick={handleNotificationMenuClose}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Room 205 checkout reminder
              </Typography>
              <Typography variant="caption" color="text.secondary">
                15 minutes ago
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem onClick={handleNotificationMenuClose}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                New restaurant order
              </Typography>
              <Typography variant="caption" color="text.secondary">
                1 hour ago
              </Typography>
            </Box>
          </MenuItem>
        </Menu>

      </Toolbar>
    </AppBar>
  );
};

export default TopNavigation;
